import { callBankNameEndpoint } from 'registration/BankInfo/BankInfoApiCalls';
import { debounce, merge, omit } from 'lodash';
import {
  formatLoanPurpose,
  serializeDateOfBirth
} from 'helpers/FormattingHelper';
import { postData } from 'helpers/RequestHelper';
import {
  reduxApplicationTealiumEventListener,
  userRegisterTealiumEvent
} from 'helpers/TealiumHelper';
import {
  revalidateNextPayrollDate,
  validateFieldForPage,
  validateLastPayrollDate
} from 'reg_redesign/helper';
import { ruleExecutor } from './redux/middleware';
import { SET_DATA, SET_ERROR } from 'helpers/Constants';
import isEmployedSelector from 'helpers/Selector';
import moment from 'moment';

const MARITAL_PROPS = [
  'isMarried',
  'spouseFirstName',
  'spouseLastName',
  'spouseAddress',
  'spouseCity',
  'spouseState',
  'spouseZip'
];

const ALL_PROPS = [
  // no validation needed but these property are on the store
  'productOwner',
  'isDisabled',
  'hideSsn',
  'marketingDisclosure',
  'productCode',
  'bankNameVisible',
  'bankNameDisabled',
  // special case because they must be validate again
  'bankName',
  'employmentStatus',
  'occupation',
  'turnUpDisclosure',
  'canSaveBankInfo',
  // special case because validation method signatures are different,
  'lastPayrollDate',
  'nextPayrollDate',
  ...MARITAL_PROPS
];

export const passwordChangeAction = (value = '', validationText, touched) => {
  return (dispatch, getState) => {
    const { email, passwordConfirmation } = getState();
    const result = validationText.map((ele) => {
      if (value === '' && touched === false) {
        return {
          text: ele.displayValue,
          status: 'unTouch'
        };
      }
      if (!ele.rule(value, email, passwordConfirmation)) {
        return {
          text: ele.displayValue,
          status: 'fail'
        };
      }
      return {
        text: ele.displayValue,
        status: 'pass'
      };
    });
    dispatch({
      type: SET_DATA,
      payload: {
        additionalProps: {
          password: {
            validationsDisplay: result
          }
        }
      }
    });
  };
};

export const clearNextPayrollDate = () => {
  return (dispatch, getState) => {
    if (getState().paymentFrequency !== 'twice_per_month') {
      dispatch({ type: SET_ERROR, payload: { nextPayrollDate: null } });
      dispatch({ type: SET_DATA, payload: { nextPayrollDate: null } });
    }
  };
};

export const validateAllFieldsForPage = (page, store) => {
  const temp = omit(store, ALL_PROPS);

  const result = Object.keys(temp).reduce((acc, key) => {
    return merge(acc, validateFieldForPage(page, key, temp));
  }, {});

  // WI case, marital information
  if (store.state === 'WI') {
    merge(result, validateFieldForPage(page, 'isMarried', store));

    if (store.isMarried === 'true') {
      MARITAL_PROPS.forEach((key) => {
        merge(result, validateFieldForPage(page, key, store));
      });
    }
  }

  if (store.productOwner === 'ccb') {
    merge(result, validateFieldForPage(page, 'occupation', store));
  }
  merge(result, validateLastPayrollDate(page, 'lastPayrollDate', store));
  if (store.paymentFrequency === 'twice_per_month') {
    merge(result, revalidateNextPayrollDate(page, 'nextPayrollDate', store));
  }
  if (isEmployedSelector(store)) {
    merge(result, validateFieldForPage(page, 'employmentStatus', store));
  }

  if (store.routingNumber) {
    merge(result, validateFieldForPage(page, 'bankName', store));
  }

  return result;
};

export const setNextPage = (callback) => {
  return (dispatch, getState) => {
    // getState
    const state = getState();
    // figure out the page we are on
    const currentPage = window.location.pathname.split('/').pop();
    // loop through page rule
    let hasNextPageChange = false;
    if (currentPage && state.pageRules?.[currentPage]?.pageRule) {
      hasNextPageChange = state.pageRules[currentPage].pageRule.some((rule) => {
        // run the rule logic to set the nextPage
        return ruleExecutor(rule, state, dispatch);
      });
    }
    if (!hasNextPageChange) {
      dispatch({
        type: SET_DATA,
        payload: {
          nextPage: state.pageRules[currentPage].defaultNextPage
        }
      });
    }
    const { nextPage } = getState();
    callback(nextPage);

    reduxApplicationTealiumEventListener(state);
  };
};

const serializeData = (store) => {
  return {
    'registration[first_name]': store.firstName,
    'registration[last_name]': store.lastName,
    'registration[email_address]': store.email,
    'registration[password]': store.password,
    'registration[mobile_phone]': store.primaryPhoneNumber,
    'registration[home_phone]': store.secondaryPhoneNumber,
    'registration[loan_purpose]': formatLoanPurpose(store.loanPurpose),
    'registration[street_address]': store.homeStreetAddress,
    'registration[secondary_address]': store.secondaryAddress,
    'registration[location][city]': store.city,
    'registration[location][state]': store.state,
    'registration[location][zip]': store.zip,
    'registration[is_married]': store.isMarried,
    'registration[is_referral]': store.isReferral,
    'registration[spouse_first_name]': store.spouseFirstName,
    'registration[spouse_last_name]': store.spouseLastName,
    'registration[spouse_street_address]': store.spouseAddress,
    'registration[spouse_location][city]': store.spouseCity,
    'registration[spouse_location][zip]': store.spouseZip,
    'registration[spouse_location][state]': store.spouseState,
    'registration[is_spouse_address_same]': store.isSpouseAddressSame,
    'registration[income_type]': store.primaryIncomeSource,
    'registration[next_payroll_date]': store.nextPayrollDate,
    'registration[last_payroll_date]': store.lastPayrollDate,
    'registration[payroll_frequency]': store.paymentFrequency,
    'registration[payroll_type]': store.paymentType,
    'registration[gross_monthly_income]': store.grossMonthlyIncome,
    'registration[occupation]': store.occupation,
    'registration[employer_name]': store.nameOfIncome,
    'registration[currently_employed]': store.employmentStatus,
    'registration[income_length_in_months]': store.lengthOfReceivingIncome,
    'registration[loan_amount]': store.loanAmount,
    'registration[date_of_birth]': serializeDateOfBirth(store.dateOfBirth),
    'registration[social_security_number]': store.socialSecurityNumber,
    'registration[routing_number]': store.routingNumber,
    'registration[account_number]': store.accountNumber,
    'registration[bank_name]': store.bankName,
    'registration[account_type]': store.accountType,
    'registration[account_length][years]': '',
    'registration[account_length][months]': store.bankAccountAge,
    'registration[disclaimers][marketing_disclosure]':
      store.marketingDisclosure,
    'registration[disclaimers][electronic_communications_disclosure]':
      store.elecCommDisclosure,
    'registration[disclaimers][turnup_offers_disclosure]':
      store.turnUpDisclosure,
    'registration[device_id]': window.AppConfig.deviceId,
    client_time: Date.now()
  };
};

// Exported for tests
export const createFormData = (data) => {
  const formData = new FormData();

  Object.keys(data).forEach((key) => {
    if (data[key] || data[key] === false) {
      formData.append(key, data[key]);
    }
  });

  return formData;
};

export const submitData = (store) => {
  const data = serializeData(store);
  const formData = createFormData(data);

  return postData({
    url: '/registrations',
    body: formData,
    contentType: 'application/x-www-form-urlencoded'
  }).catch((e) => {
    if (e.response.status === 440 || e.response.status === 422) {
      window.toastr.error(
        "<a onclick='location.reload()'>Try refreshing the page</a>",
        'Looks like your session is stale',
        {
          timeOut: 0,
          escapeHtml: false
        }
      );
    }
  });
};

const debouncedSubmitData = debounce(submitData, 500);

const debouncedSetNextPage = debounce((dispatch, callback) => {
  dispatch(setNextPage(callback));

  dispatch({
    type: SET_DATA,
    payload: {
      submitLoading: false
    }
  });
}, 500);

export const submitAction = (page, callback) => {
  return (dispatch, getState) => {
    const state = getState();

    if (page === 'page-1') {
      dispatch(setNextPage(callback));
      return;
    }

    if (page === 'page-2' && state.isReferral === 'true') {
      window.trackAttributionTouch({ Other_type: 'RAF' });
    }

    dispatch({
      type: SET_DATA,
      payload: {
        submitLoading: true
      }
    });
    debouncedSubmitData(state, dispatch, callback);
    debouncedSetNextPage(dispatch, callback);
  };
};

export const submitApplication = () => {
  return async (dispatch, getState) => {
    const startTime = moment();
    let state = getState();

    dispatch({
      type: SET_DATA,
      payload: {
        submitLoading: true
      }
    });

    // default last payroll date when null as required in salesforce
    if (!state.lastPayrollDate) {
      dispatch({
        type: SET_DATA,
        payload: {
          lastPayrollDate: '01/01/1970',
          nextPayrollDate: moment().add(1, 'days').format('MM/DD/YYYY')
        }
      });
    }

    // default pay frequency when null as required in salesforce
    if (!state.paymentFrequency) {
      dispatch({
        type: SET_DATA,
        payload: {
          paymentFrequency: 'weekly'
        }
      });
    }

    state = getState();

    await submitData(state);
    try {
      const result = await postData({
        url: '/registrations/apply',
        body: {
          client_time: Date.now(),
          password: state.password,
          product_code: state.productCode,
          product_owner: state.productOwner,
          react: true
        }
      });
      if (result.data.application_id) {
        window.postHeapAnalytic('identify', result.data.contact_id);
        window.postHeapAnalytic('addUserProperties', {
          email: result.data.login_info.email_address,
          state: result.data.login_info.state
        });
        window.postHeapAnalytic('track', 'submitApplication', {
          application_id: result.data.application_id
        });
      }
      userRegisterTealiumEvent(state);
      window.postHeapAnalytic('track', '[submit][registration][SUCCESS]', {
        email: state.email,
        state: state.state,
        partner_id: window.AppConfig.partner?.partner_id,
        event_data: window.AppConfig.partner?.partner_event_data,
        apply_duration: Math.round(moment().diff(startTime) / 1000),
        application_id: result.data.application_id,
        contact_id: result.data.contact_id,
        apply_type: result.data.apply_type
      });
      window.location.href = result.data.apply_redirect_path;
    } catch (e) {
      dispatch({
        type: SET_DATA,
        payload: {
          submitLoading: false
        }
      });
      window.postHeapAnalytic('track', '[submit][registration][ERROR]', {
        email: state.email,
        state: state.state,
        partner_id: window.AppConfig.partner?.partner_id,
        event_data: window.AppConfig.partner?.partner_event_data,
        error_msg: e,
        apply_duration: Math.round(moment().diff(startTime) / 1000)
      });
      window.Raven?.captureException(e);
      const phone = window.AppConfig.state.phone;
      window.toastr?.error(
        `Refresh and submit again, Call us at ${phone} if this issue persists.`,
        'Something went wrong.'
      );
    }
  };
};

export const changeLastPayrollDateAction = () => {
  return (dispatch, getState) => {
    const lastPayrollDate = getState().lastPayrollDate;
    const minDate =
      moment() < moment(lastPayrollDate, 'MM/DD/YYYY').add(10, 'day')
        ? moment(lastPayrollDate, 'MM/DD/YYYY')
            .add(10, 'day')
            .diff(moment(), 'days')
        : 0;
    const maxDate = moment(lastPayrollDate, 'MM/DD/YYYY')
      .add(18, 'day')
      .diff(moment(), 'days');
    dispatch({
      type: SET_DATA,
      payload: {
        additionalProps: {
          nextPayrollDate: {
            minDate,
            maxDate
          }
        }
      }
    });
  };
};

export const blurRoutingNumberAction = (val, id, isOnMount) => {
  return async (dispatch, getState) => {
    const state = getState();
    const bankNameId = 'bankName';
    const routingNumberId = 'routingNumber';

    if (!state.errors[routingNumberId] && state[routingNumberId]) {
      dispatch({
        type: SET_DATA,
        payload: {
          fieldsToDisplay: {
            [bankNameId]: true
          }
        }
      });
      try {
        const bankName = await callBankNameEndpoint(state[routingNumberId]);
        if (!bankName) throw new Error('Request failed.');
        dispatch({
          type: SET_DATA,
          payload: { [bankNameId]: bankName }
        });
        dispatch({
          type: SET_DATA,
          payload: {
            additionalProps: {
              [bankNameId]: {
                isDisabled: true
              }
            }
          }
        });
        dispatch({
          type: SET_ERROR,
          payload: { [bankNameId]: '', [routingNumberId]: '' }
        });
      } catch (e) {
        window.Raven?.captureException(e);
        if (!isOnMount) {
          dispatch({
            type: SET_DATA,
            payload: { [bankNameId]: '' }
          });
        }
        dispatch({
          type: SET_DATA,
          payload: {
            fieldsToDisplay: {
              [bankNameId]: true
            }
          }
        });
        dispatch({
          type: SET_DATA,
          payload: {
            additionalProps: {
              [bankNameId]: {
                isDisabled: false
              }
            }
          }
        });
      }
    } else {
      dispatch({
        type: SET_DATA,
        payload: {
          fieldsToDisplay: {
            [bankNameId]: false
          }
        }
      });
      dispatch({
        type: SET_DATA,
        payload: { [bankNameId]: '' }
      });
      dispatch({
        type: SET_ERROR,
        payload: { [bankNameId]: '' }
      });
    }
  };
};

export const changeSpouseIsSameAddress = (value) => {
  return (dispatch, getState) => {
    const state = getState();
    if (state.isSpouseAddressSame === value || value === '') {
      return;
    }
    dispatch({
      type: SET_DATA,
      payload: {
        isSpouseAddressSame: value
      }
    });
    if (value) {
      dispatch({
        type: SET_DATA,
        payload: {
          spouseAddress: state.homeStreetAddress,
          spouseCity: state.city,
          spouseState: state.state,
          spouseZip: state.zip,
          isDisabled: {
            spouseAddress: true,
            spouseCity: true,
            spouseState: true,
            spouseZip: true
          }
        }
      });

      dispatch({
        type: SET_ERROR,
        payload: {
          spouseAddress: '',
          spouseState: '',
          spouseCity: '',
          spouseZip: ''
        }
      });
    } else {
      dispatch({
        type: SET_DATA,
        payload: {
          spouseAddress: '',
          spouseCity: '',
          spouseState: '',
          spouseZip: '',
          isDisabled: {
            spouseAddress: false,
            spouseCity: false,
            spouseState: false,
            spouseZip: false
          }
        }
      });
    }
  };
};
